<script setup>
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import { useMessageStore } from '~/stores/messages'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fragen teaser is desktop-only in the redesign (CN-12074); do not render it on mobile/tablet
const isDesktop = useMediaQuery('(min-width: 1024px)')

// Markdown
const { $md } = useNuxtApp()
const pageSubline1 = computed(() => $md.renderInline(staticKeys.value?.center_selector_teaser_headline_web_markdown || ''))
const pageSubline2 = computed(() => $md.renderInline(staticKeys.value?.parking_card_teaser_headline_web_markdown || ''))

const heroTeaserButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'internal',
        link: staticKeys.value?.parking_card_btn_link_web,
        active: true
    }
})
</script>

<template>
    <section class="hero-teaser">
        <div class="hero-teaser-container">
            <div class="center-selector-teaser-wrapper">
                <div class="center-selector-teaser-container">
                    <h2 v-html="pageSubline1" />
                    <p>{{ staticKeys?.center_selector_teaser_txt_web }}</p>

                    <div class="center-selector">
                        <CenterSelector :center-selector-config="{ position: 'content' }" />
                    </div>
                </div>
            </div>

            <ClientOnly>
                <div v-if="isDesktop" class="hero-teaser-info-txt">
                    <h2 v-html="pageSubline2" />
                    <p>{{ staticKeys?.parking_card_teaser_txt_web }}</p>

                    <div class="hero-teaser-info-txt-btn-bar">
                        <BaseButton :base-button-config="heroTeaserButtonConfig">
                            {{ staticKeys?.parking_card_btn_web }}
                        </BaseButton>
                    </div>
                </div>
            </ClientOnly>
        </div>
    </section>
</template>

<style lang="scss">
.hero-teaser {
    position: relative;
    z-index: z('center-selector-teaser');

    @include fullbleed;

    @include bp(large1) {
        background: $gradient-teal;
        padding: 80px 0;
    }

    // start page: the band is the last block and grows to the footer; only the color fills that space,
    // the content keeps its natural height. On large screens the teal comes from the page container instead.
    [data-route="home"] & {
        align-content: start;
        background: $gradient-teal;

        @include bp(large1) {
            background: transparent;
        }
    }
}

.hero-teaser-container {
    @include content-grid;
}

.center-selector-teaser-wrapper {
    grid-column: 1/ span 5;
    grid-row: 1;
    background: $gradient-teal;
    margin: 0 -4vw;

    @include bp(medium1) {
        grid-column: 1/ span 8;
    }

    @include bp(large1) {
        grid-column: 1/ span 4;
        background: none;
        margin: 0;
    }

    [data-route="home"] & {
        background: none; // the section carries the gradient here, see .hero-teaser
    }
}

.center-selector-teaser-container {
    margin: 0 4vw;
    padding: 40px 0 30px;

    @include content-grid;

    @include bp(medium1) {
        padding: 80px 0 50px;
    }

    @include bp(large1) {
        margin: 0;
        padding: 0;
        height: 100%;
        display: flex;
        flex-flow: row wrap;
    }

    h2 {
        grid-column: 1/ span 5;

        @include superheadline-h2($margin: 0 0 10px);

        @include bp(medium1) {
            grid-column: 1/ span 4;
            margin: 0 0 4px;
        }

        @include bp(large1) {
            max-width: 100%;
            width: 100%;
            margin: 0;
        }
    }

    p {
        margin: 0 0 30px;
        grid-column: 1/ span 5;

        @include body2-medium; // redesign (CN-12074): Figma band description is 14px (Mulish Medium), was body1-medium (16px)

        @include bp(medium1) {
            grid-column: 1/ span 4;
            margin: 0 0 30px;
            max-width: 90%; // keep the description off the selector in the right column
        }

        @include bp(large1) {
            max-width: 100%;
            width: 100%;
            margin: 0;
        }
    }
}

.center-selector {
    grid-column: 1/ span 5;
    margin-top: auto;
    width: 100%;

    @include bp(medium1) {
        grid-column: 5 / span 4; // Figma M: selector in the right column, bottom-aligned
        grid-row: 1 / span 2;
        align-self: end;
        margin-top: 0;
    }

    @include bp(large1) {
        grid-row: auto;
        align-self: auto;
        margin-top: auto;
    }
}

.hero-teaser-info-txt {
    grid-column: 9/ span 4;
    grid-row: 1;
    display: flex;
    width: 100%;
    height: 100%;
    flex-flow: row wrap;

    h2 {
        @include superheadline-h2;
    }

    p {
        margin: 0 0 60px;

        @include body1-medium;
    }
}

.hero-teaser-info-txt-btn-bar {
    margin: auto 0 0;
}
</style>
